import { Suspense, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useHtmlOverflowHidden } from "@/hooks/use-html-overflow-hidden";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, Globe, Sparkles } from "lucide-react";
import { ChangelogEditorPanel } from "@/components/changelog/changelog-editor-panel";
import { ChangelogPreviewPanel } from "@/components/changelog/preview/changelog-preview-panel";
import { PanelResizeHandle } from "@/components/articles/publish/panel-resize-handle";
import { CategoryBadge } from "@/components/changelog/category-badge";
import { ChangelogAiDraftDialog } from "@/components/changelog/changelog-ai-draft-dialog";
import {
  changelogCategoriesQueryOptions,
  useCreateChangelogEntry,
} from "@/hooks/use-changelog";
import {
  changelogEntrySchema,
  validateBilingualContent,
  type ChangelogEntryFormValues,
} from "@/lib/validation/changelog-schema";
import type { GenerateChangelogDraftResult } from "@/types/changelog";
import { toast } from "sonner";

const SPLIT_VIEW_BREAKPOINT = 1024;

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(
      `(max-width: ${SPLIT_VIEW_BREAKPOINT - 1}px)`,
    );
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mql.addEventListener("change", onChange);
    setIsNarrow(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isNarrow;
}

function ChangelogNewForm() {
  useHtmlOverflowHidden();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const { data: categories } = useSuspenseQuery(
    changelogCategoriesQueryOptions(),
  );
  const createMutation = useCreateChangelogEntry();

  const [splitRatio, setSplitRatio] = useState(0.5);
  const [mobileTab, setMobileTab] = useState<"editor" | "preview">("editor");
  const [lang, setLang] = useState<"ko" | "en">("ko");
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  const methods = useForm<ChangelogEntryFormValues>({
    resolver: zodResolver(changelogEntrySchema),
    defaultValues: {
      titleKo: "",
      titleEn: "",
      bodyKo: "",
      bodyEn: "",
      featuredImageUrl: "",
      categoryIds: [],
    },
  });

  const handleDraftGenerated = useCallback(
    (draft: GenerateChangelogDraftResult) => {
      const { titleKo, bodyKo } = methods.getValues();
      const hasExistingContent = titleKo.trim() || bodyKo.trim();

      if (hasExistingContent) {
        const confirmed = window.confirm(
          "기존에 작성된 내용이 있습니다. AI 초안으로 덮어쓰시겠습니까?",
        );
        if (!confirmed) return;
      }

      methods.setValue("titleKo", draft.titleKo);
      methods.setValue("titleEn", draft.titleEn);
      methods.setValue("bodyKo", draft.bodyKo);
      methods.setValue("bodyEn", draft.bodyEn);
      methods.setValue("categoryIds", draft.categoryIds, {
        shouldValidate: true,
      });
      toast.success("AI 초안이 에디터에 적용되었습니다");
    },
    [methods],
  );

  const selectedCategoryIds = methods.watch("categoryIds");

  const toggleCategory = (categoryId: string) => {
    const current = methods.getValues("categoryIds");
    const next = current.includes(categoryId)
      ? current.filter((id) => id !== categoryId)
      : [...current, categoryId];
    methods.setValue("categoryIds", next, { shouldValidate: true });
  };

  const onFormError = (errors: Record<string, { message?: string }>) => {
    const messages = Object.values(errors)
      .map((e) => e?.message)
      .filter(Boolean);

    toast.error("입력값을 확인해주세요", {
      description: messages.join(", "),
    });
  };

  const handleSubmit = async (
    values: ChangelogEntryFormValues,
    publishNow: boolean,
  ) => {
    if (publishNow) {
      const { valid, missingFields } = validateBilingualContent(values);

      if (!valid) {
        toast.error("한국어와 영문을 모두 작성해야 발행할 수 있습니다", {
          description: `미작성 항목: ${missingFields.join(", ")}`,
        });
        return;
      }
    }

    try {
      await createMutation.mutateAsync({
        title: { ko: values.titleKo, en: values.titleEn || null },
        body: { ko: values.bodyKo, en: values.bodyEn || null },
        featuredImageUrl: values.featuredImageUrl || undefined,
        categoryIds: values.categoryIds,
        publishNow,
      });
      navigate("/changelog");
    } catch {
      // 에러는 mutation onError에서 toast로 처리됨
    }
  };

  const handleResize = useCallback(
    (ratio: number | ((prev: number) => number)) => {
      setSplitRatio((prev) =>
        typeof ratio === "function" ? ratio(prev) : ratio,
      );
    },
    [],
  );

  return (
    <FormProvider {...methods}>
      <form className="-m-4 flex h-[calc(100vh-var(--header-height,64px))] flex-col md:-m-6">
        {/* Header */}
        <div className="flex items-center gap-4 border-b px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/changelog")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            목록
          </Button>

          <div className="h-4 w-px bg-border" />

          {/* Language tabs */}
          <Tabs
            value={lang}
            onValueChange={(v) => setLang(v as "ko" | "en")}
          >
            <TabsList className="h-8">
              <TabsTrigger value="ko" className="px-2.5 text-xs">
                한국어
              </TabsTrigger>
              <TabsTrigger value="en" className="px-2.5 text-xs">
                English
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="h-4 w-px bg-border" />

          {/* AI Draft Generation */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAiDialogOpen(true)}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            AI 초안
          </Button>

          <div className="h-4 w-px bg-border" />

          {/* Category badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat._id}
                type="button"
                onClick={() => toggleCategory(cat._id)}
                className="cursor-pointer"
              >
                <CategoryBadge
                  name={cat.name.ko}
                  color={cat.color}
                  className={
                    selectedCategoryIds.includes(cat._id)
                      ? "ring-2 ring-offset-1"
                      : "opacity-50"
                  }
                />
              </button>
            ))}
          </div>
        </div>

        {isNarrow ? (
          /* Mobile: Tab-based layout */
          <Tabs
            value={mobileTab}
            onValueChange={(v) => setMobileTab(v as "editor" | "preview")}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <TabsList className="mx-4 mt-2 grid w-auto grid-cols-2">
              <TabsTrigger value="editor">편집</TabsTrigger>
              <TabsTrigger value="preview">미리보기</TabsTrigger>
            </TabsList>

            <TabsContent value="editor" className="mt-0 flex-1 overflow-hidden">
              <ChangelogEditorPanel lang={lang} />
            </TabsContent>

            <TabsContent
              value="preview"
              className="mt-0 flex-1 overflow-hidden"
            >
              <ChangelogPreviewPanel categories={categories} lang={lang} />
            </TabsContent>
          </Tabs>
        ) : (
          /* Desktop: Side-by-side split view */
          <div className="flex flex-1 overflow-hidden">
            <div
              className="overflow-hidden"
              style={{
                flexBasis: `${splitRatio * 100}%`,
                minWidth: "360px",
              }}
            >
              <ChangelogEditorPanel lang={lang} />
            </div>

            <PanelResizeHandle onResize={handleResize} />

            <div
              className="overflow-hidden"
              style={{
                flexBasis: `${(1 - splitRatio) * 100}%`,
                minWidth: "360px",
              }}
            >
              <ChangelogPreviewPanel categories={categories} lang={lang} />
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="bg-background flex items-center justify-end gap-3 border-t px-4 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              methods.handleSubmit((v) => handleSubmit(v, false), onFormError)()
            }
            disabled={createMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {createMutation.isPending ? "저장 중..." : "초안 저장"}
          </Button>
          <Button
            type="button"
            onClick={() =>
              methods.handleSubmit((v) => handleSubmit(v, true), onFormError)()
            }
            disabled={createMutation.isPending}
          >
            <Globe className="mr-2 h-4 w-4" />
            {createMutation.isPending ? "발행 중..." : "즉시 발행"}
          </Button>
        </div>
      </form>

      <ChangelogAiDraftDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        onDraftGenerated={handleDraftGenerated}
      />
    </FormProvider>
  );
}

export default function ChangelogNewPage() {
  return (
    <Suspense
      fallback={
        <div className="p-8 text-center text-muted-foreground">로딩 중...</div>
      }
    >
      <ChangelogNewForm />
    </Suspense>
  );
}
